import { useAnonymize } from '@/composables/useAnonymize'

/**
 * Studio mode's delivery layer: a DOM sweep.
 *
 * The per-component approach (`anonCompany()` / `anonymize()` threaded through
 * templates) covers the radar, early warnings and the alert drawer — and
 * nothing else. impact-v5 is 23 components and ~5,400 lines with zero
 * coverage, because it was built after the composable and nobody went back.
 * Threading it through by hand would rot the same way again.
 *
 * So instead: one observer over the app root that rewrites text nodes and the
 * few attributes that surface names (title/alt/placeholder/aria-label). Every
 * screen is covered, including screens nobody thought about, and a new
 * component cannot forget to opt in.
 *
 * The existing per-component calls stay. They are idempotent — an invented
 * name is never a key in the map, so a second pass over it is a no-op — and
 * they get names into the map before the sweep runs.
 *
 * Exiting reloads the page. Restoring original text would mean keeping a
 * shadow copy of every node; a reload is exact and costs a second.
 */

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE'])
const ATTRS = ['title', 'alt', 'placeholder', 'aria-label']

let observer = null
let writing = false      // guards against reacting to our own mutations
let queued = false

let personPass = (t) => t   // set from the composable on first use

function applyAll(text, reps) {
  if (!text || !text.trim()) return text
  let out = text
  for (const [re, fake] of reps) {
    re.lastIndex = 0
    out = out.replace(re, fake)
  }
  // Executives named in report prose ("CEO Jane Doe") are real people and
  // would otherwise be the one identity the sweep left on screen.
  return personPass(out)
}

function sweepNode(root, reps) {
  if (!reps.length) return

  // Text nodes
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) =>
      n.parentElement && !SKIP_TAGS.has(n.parentElement.tagName) &&
      !n.parentElement.closest('[data-studio-skip]')
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  })
  const edits = []
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const next = applyAll(n.nodeValue, reps)
    if (next !== n.nodeValue) edits.push([n, next])
  }

  // Name-bearing attributes
  const attrEdits = []
  const scope = root.nodeType === Node.ELEMENT_NODE ? root : document.body
  const els = [scope, ...scope.querySelectorAll('[title],[alt],[placeholder],[aria-label]')]
  for (const el of els) {
    if (!el.getAttribute) continue
    for (const a of ATTRS) {
      const v = el.getAttribute(a)
      if (!v) continue
      const next = applyAll(v, reps)
      if (next !== v) attrEdits.push([el, a, next])
    }
  }

  writing = true
  for (const [n, v] of edits) n.nodeValue = v
  for (const [el, a, v] of attrEdits) el.setAttribute(a, v)
  writing = false
}

export function useStudioMode() {
  const anon = useAnonymize()
  const {
    isAnonymized, toggle, registerCompanies, registerPortfolios, replacements,
  } = anon
  personPass = anon.personPass

  /**
   * Re-sweep the whole document on the next frame, coalescing bursts.
   *
   * rAF alone is not enough: a hidden page (another tab fronted, the window
   * minimised behind a recorder) stops firing it entirely, so the sweep would
   * sit queued while the DOM changed underneath it. A timer backstop races the
   * frame and whichever wins does the work — `queued` keeps it to one pass.
   */
  const scheduleSweep = () => {
    if (queued) return
    queued = true
    const run = () => {
      if (!queued) return
      queued = false
      if (isAnonymized.value) sweepNode(document.body, replacements())
    }
    requestAnimationFrame(run)
    setTimeout(run, 50)
  }

  /**
   * Seed the name map before anything is swept. Without this the map fills
   * lazily from whatever rendered first, and free text mentioning a company
   * that has not rendered yet slips through untouched.
   */
  const seed = ({ companies = [], portfolios = [] } = {}) => {
    registerPortfolios(portfolios)
    registerCompanies(companies)
  }

  const start = () => {
    if (!import.meta.client || observer) return
    observer = new MutationObserver((records) => {
      if (writing) return
      for (const r of records) {
        if (r.type === 'characterData' || r.addedNodes.length || r.type === 'attributes') {
          scheduleSweep()
          return
        }
      }
    })
    observer.observe(document.body, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ATTRS,
    })
    scheduleSweep()
  }

  const stop = () => {
    observer?.disconnect()
    observer = null
  }

  /** Enter studio mode: flag on, seed, sweep. No reload — it applies live. */
  const enter = (seeds) => {
    if (isAnonymized.value) return
    toggle()
    seed(seeds)
    start()
  }

  /** Leave studio mode: flag off, then reload so the real names come back. */
  const leave = () => {
    if (!isAnonymized.value) return
    toggle()
    stop()
    if (import.meta.client) window.location.reload()
  }

  return { isStudio: isAnonymized, enter, leave, seed, start, stop, scheduleSweep }
}
